import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Space, Spin, Typography } from 'antd';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import type { PDFDocumentLoadingTask, PDFDocumentProxy, RenderTask } from 'pdfjs-dist';
import api from '../services/api';
import { getApiErrorMessage } from '../utils/apiError';
import { loadPdfJs, pdfAssetsBase, readPdfBytes } from '../utils/trainingPdf';
import './TrainingPdfViewer.css';

const TrainingPdfViewer: React.FC<{ documentId: number; title: string; adminPreview?: boolean }> = ({ documentId, title, adminPreview = false }) => {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [pdf, setPdf] = useState<PDFDocumentProxy | null>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [width, setWidth] = useState(0);
  const [loading, setLoading] = useState(true);
  const [rendering, setRendering] = useState(false);
  const [pageText, setPageText] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    const measure = () => setWidth(Math.max(0, Math.floor(host.clientWidth - 24)));
    measure();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    observer?.observe(host);
    window.addEventListener('resize', measure);
    return () => { observer?.disconnect(); window.removeEventListener('resize', measure); };
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    let task: PDFDocumentLoadingTask | null = null;
    setLoading(true); setPdf(null); setPageNumber(1); setError(''); setPageText('');
    const load = async () => {
      try {
        const { data } = await api.get<Blob>(`/video/${adminPreview ? 'admin/' : ''}documents/${documentId}/file`, { responseType: 'blob', signal: controller.signal });
        const bytes = await readPdfBytes(data);
        if (controller.signal.aborted) return;
        const library = await loadPdfJs();
        if (controller.signal.aborted) return;
        task = library.getDocument({
          data: bytes, enableXfa: false, useWorkerFetch: true,
          cMapUrl: `${pdfAssetsBase}cmaps/`, standardFontDataUrl: `${pdfAssetsBase}standard_fonts/`,
          wasmUrl: `${pdfAssetsBase}wasm/`, iccUrl: `${pdfAssetsBase}iccs/`,
        });
        const document = await task.promise;
        if (!controller.signal.aborted) setPdf(document);
      } catch (err) {
        if (!controller.signal.aborted) setError(getApiErrorMessage(err, 'Не удалось показать PDF. Его можно скачать кнопкой выше.'));
      } finally { if (!controller.signal.aborted) setLoading(false); }
    };
    load();
    return () => { controller.abort(); task?.destroy().catch(() => undefined); };
  }, [documentId, adminPreview]);

  useEffect(() => {
    if (!pdf || width <= 0) return;
    let active = true;
    let task: RenderTask | null = null;
    setRendering(true); setError(''); setPageText('');
    const render = async () => {
      const buffer = window.document.createElement('canvas');
      try {
        const page = await pdf.getPage(pageNumber);
        if (!active) return;
        const original = page.getViewport({ scale: 1 });
        const viewport = page.getViewport({ scale: width / original.width });
        const outputScale = Math.min(window.devicePixelRatio || 1, 2, Math.sqrt(8000000 / (viewport.width * viewport.height)));
        buffer.width = Math.max(1, Math.floor(viewport.width * outputScale));
        buffer.height = Math.max(1, Math.floor(viewport.height * outputScale));
        task = page.render({ canvas: buffer, viewport, transform: [outputScale, 0, 0, outputScale, 0, 0] });
        await task.promise;
        const canvas = canvasRef.current;
        if (!active || !canvas) return;
        canvas.width = buffer.width; canvas.height = buffer.height;
        canvas.style.width = `${Math.floor(viewport.width)}px`; canvas.style.height = `${Math.floor(viewport.height)}px`;
        const context = canvas.getContext('2d');
        if (!context) throw new Error('Браузер не поддерживает просмотр PDF. Скачайте файл кнопкой выше.');
        context.drawImage(buffer, 0, 0);
        setRendering(false);
        try {
          const text = await page.getTextContent();
          if (active) setPageText(text.items.map(item => 'str' in item ? item.str : '').join(' '));
        } catch { /* A text-extraction failure must not hide a successfully rendered page. */ }
      } catch (err: any) {
        if (active && err?.name !== 'RenderingCancelledException') {
          setError(getApiErrorMessage(err, 'Не удалось показать страницу PDF. Скачайте файл кнопкой выше.'));
          setRendering(false);
        }
      } finally { buffer.width = 0; buffer.height = 0; }
    };
    render();
    return () => { active = false; task?.cancel(); };
  }, [pdf, pageNumber, width]);

  return <div className="training-pdf-viewer">
    <div className="training-pdf-toolbar">
      <Space size={8}>
        <Button size="small" aria-label="Предыдущая страница PDF" icon={<LeftOutlined />} disabled={!pdf || pageNumber <= 1} onClick={() => setPageNumber(value => value - 1)} />
        <Typography.Text aria-live="polite">Страница {pageNumber} из {pdf?.numPages || '…'}</Typography.Text>
        <Button size="small" aria-label="Следующая страница PDF" icon={<RightOutlined />} disabled={!pdf || pageNumber >= pdf.numPages} onClick={() => setPageNumber(value => value + 1)} />
      </Space>
      <Typography.Text type="secondary">По ширине</Typography.Text>
    </div>
    {error && <Alert type="error" showIcon message={error} />}
    <div className="training-pdf-pages" ref={hostRef} aria-busy={loading || rendering}>
      {(loading || rendering) && <div className="training-pdf-loading"><Spin size="small" /><span>Загрузка страницы…</span></div>}
      <canvas ref={canvasRef} role="img" aria-label={`${title}. Страница ${pageNumber}`} hidden={!pdf || Boolean(error)} />
    </div>
    {pageText && <div className="training-pdf-accessible-text">{pageText}</div>}
  </div>;
};

export default TrainingPdfViewer;
